import { Button, CalendarPicker, Select } from '@appkit4/react-components';
import { useCallback, useRef } from 'react';
import { useFormikContext } from 'formik';
import { NewDeclarationFormValues } from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { useAbsenceReasons } from 'contexts/AbsenceReasonsProvider/AbsenceReasonsProvider';
import useFocusOnRender from 'helpers/useFocusOnRender';

export type UnforeseenDatesRowProps = {
    index: string;
    unforeseenAbsence?: {
        declarationId?: string | null;
        reasonForAbsence: string;
        startDate: Date | undefined;
    };
    disabledDates: Date[];
};

type SelectValue = ValueType | ValueType[];
type ValueType = string | number;

const UnforeseenDatesRow = ({
    index,
    unforeseenAbsence,
    disabledDates,
}: UnforeseenDatesRowProps) => {
    const { values, setFieldValue } =
        useFormikContext<NewDeclarationFormValues>();
    const { unforeseenAbsenceReasons } = useAbsenceReasons();
    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRender(inputRef);

    const today = new Date();
    const maxDate = new Date();
    const minDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    maxDate.setDate(today.getDate());

    const onReasonChange = useCallback(
        (index: string, selectedValue: SelectValue) => {
            const updatedUnforeseenAbsenceArray =
                values.unforeseenAbsences?.map((unforeseenAbsence, idx) => {
                    const itemIndex =
                        unforeseenAbsence.declarationId || idx.toString();
                    if (itemIndex === index) {
                        return {
                            ...unforeseenAbsence,
                            reasonForAbsence: selectedValue as string,
                        };
                    }
                    return unforeseenAbsence;
                });

            setFieldValue('unforeseenAbsences', updatedUnforeseenAbsenceArray);
        },
        [setFieldValue, values.unforeseenAbsences]
    );

    const onDateChange = useCallback(
        (index: string, selectedDate: Date) => {
            if (!selectedDate) return;

            const updatedUnforeseenAbsences = values.unforeseenAbsences?.map(
                (item, idx) => {
                    const itemIndex = item.declarationId || idx.toString();
                    if (itemIndex === index) {
                        return {
                            ...item,
                            startDate: selectedDate,
                        };
                    }
                    return item;
                }
            );

            setFieldValue('unforeseenAbsences', updatedUnforeseenAbsences);
        },
        [setFieldValue, values.unforeseenAbsences]
    );

    const onRemoveDate = useCallback(
        (index: string) => {
            if (
                values.unforeseenAbsences !== undefined &&
                Array.isArray(values.unforeseenAbsences)
            ) {
                setFieldValue('declarationIdsToDelete', [
                    ...(values.declarationIdsToDelete || []),
                    index,
                ]);

                const updatedRows =
                    values.unforeseenAbsences?.filter(
                        (unforeseenAbsence, idx) => {
                            const itemIndex =
                                unforeseenAbsence.declarationId ||
                                idx.toString();
                            return itemIndex !== index;
                        }
                    ) || [];

                setFieldValue('unforeseenAbsences', updatedRows);
            }
        },
        [
            setFieldValue,
            values.unforeseenAbsences,
            values.declarationIdsToDelete,
        ]
    );

    return (
        <div
            key={index}
            className="calender-picker-div"
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 0.2fr',
            }}
        >
            <div className="pe-4">
                <Select
                    inputRef={inputRef}
                    key={`${index}-${
                        unforeseenAbsence?.reasonForAbsence || ''
                    }`}
                    data={unforeseenAbsenceReasons?.map(
                        (reason) => reason.reason
                    )}
                    placeholder="Please select"
                    value={unforeseenAbsence?.reasonForAbsence || ''}
                    onSelect={(selectedValue) =>
                        onReasonChange(index, selectedValue)
                    }
                    defaultValue={unforeseenAbsence?.reasonForAbsence || ''}
                />
            </div>

            <div className="date-main">
                <CalendarPicker
                    key={`${index}-${
                        unforeseenAbsence?.startDate
                            ? unforeseenAbsence?.startDate
                            : ''
                    }`}
                    value={unforeseenAbsence?.startDate || undefined}
                    useCustomValidation={true}
                    onChange={(selectedDate: Date) => {
                        onDateChange(index, selectedDate);
                    }}
                    placeholder="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    locale="en"
                    fieldTitle="DD/MM/YYYY"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabledDays={[0, 6]}
                    disabledDates={disabledDates}
                    defaultValue={unforeseenAbsence?.startDate ?? undefined}
                />
            </div>

            <div>
                {values.unforeseenAbsences?.length &&
                    values.unforeseenAbsences?.length > 1 && (
                        <Button
                            add
                            kind="text"
                            icon="Appkit4-icon icon-delete-outline"
                            onClick={() => onRemoveDate(index)}
                        />
                    )}
            </div>
        </div>
    );
};

export default UnforeseenDatesRow;
